import { inject, Injectable } from '@angular/core';
import { NavController, ToastController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import { FilterActions } from '../../../image-gallery/filter/redux/filter.actions';
import { FilterSelectors } from '../../../image-gallery/filter/redux/filter.reducer';
import { FilterService } from '../../../image-gallery/filter/services/filter.service';
import { GalleryFilterDataService } from '../../../image-gallery/filter/services/gallery-filter-data.service';
import { GalleryActions } from '../../../image-gallery/redux/gallery.actions';
import { GalleryService } from '../../../image-gallery/services/gallery.service';
import { AlbumActions } from '../../redux/album.actions';
import { EditAlbumActions } from './edit-album.actions';
import { EditAlbumSelectors } from './edit-album.reducer';

@Injectable()
export class EditAlbumEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly navController = inject(NavController);
  private readonly toastController = inject(ToastController);
  private readonly translocoService = inject(TranslocoService);
  private readonly galleryService = inject(GalleryService);
  private readonly filterService = inject(FilterService);
  private readonly galleryFilterDataService = inject(GalleryFilterDataService);

  startEditAlbum$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EditAlbumActions.startEditAlbum),
        tap(() => this.store.dispatch(AlbumActions.closeAlbumOptions())),
        switchMap(({ albumId }) =>
          this.actions$.pipe(
            ofType(AlbumActions.albumOptionsClosed),
            take(1),
            tap(() => void this.navController.navigateForward([`/home/gallery/${albumId}/edit`]))
          )
        )
      ),
    { dispatch: false }
  );

  editAlbumPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditAlbumActions.editAlbumPageEntered),
      switchMap(() =>
        this.galleryFilterDataService.fetchFilterCameras$().pipe(
          map(({ cameras }) => EditAlbumActions.editAlbumPageEnteredSucceeded({ cameras })),
          catchError((error: unknown) => of(EditAlbumActions.editAlbumPageEnteredFailed({ error })))
        )
      )
    )
  );

  // Album Options visible again on Back/Cancel, but presenting it here would race
  // the still-in-flight page transition — GalleryPage's ionViewWillEnter consumes the reopen flag.
  discardEditAlbum$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EditAlbumActions.discardEditAlbum),
        withLatestFrom(this.store.select(EditAlbumSelectors.originalFilter)),
        tap(([, originalFilter]) => {
          this.store.dispatch(FilterActions.applyCustomAlbumFilter({ filter: originalFilter, relatedCameraId: undefined }));
          this.store.dispatch(AlbumActions.markAlbumOptionsPendingReopen());
          this.navController.back();
        })
      ),
    { dispatch: false }
  );

  saveAlbumEdits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditAlbumActions.saveAlbumEdits),
      withLatestFrom(this.store.select(FilterSelectors.applied)),
      switchMap(([{ albumId, name }, applied]) => {
        // Custom albums cannot hold a date/period filter, so it's never sent.
        const { period, ...filterParameters } = applied;
        const filter = this.filterService.convertToApiFormat(filterParameters);

        return this.galleryService.updateAlbum$(albumId, name, filter).pipe(
          map(() => EditAlbumActions.saveAlbumEditsSucceeded({ albumId, name, filter })),
          catchError((error: unknown) => of(EditAlbumActions.saveAlbumEditsFailed({ error })))
        );
      })
    )
  );

  saveAlbumEditsSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EditAlbumActions.saveAlbumEditsSucceeded),
        tap(({ albumId, name, filter }) => {
          this.store.dispatch(AlbumActions.albumUpdated({ albumId, name, filter }));
          this.store.dispatch(GalleryActions.albumRenamed({ title: name }));
          this.store.dispatch(AlbumActions.closeAlbumOptions());
          this.navController.back();
        })
      ),
    { dispatch: false }
  );

  saveAlbumEditsFailedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EditAlbumActions.saveAlbumEditsFailed),
        switchMap(() =>
          from(
            this.toastController.create({
              message: this.translocoService.translate('gallery.albums.edit.saveFailed'),
              color: 'danger',
              position: 'bottom',
              duration: 1500,
            })
          ).pipe(tap(toast => toast.present()))
        )
      ),
    { dispatch: false }
  );
}
